import $ from 'jquery'
import _ from 'lodash'
import Collapsible from 'scoreboard/components/collapsible'
import i18n from 'scoreboard/components/i18n'
import panelHeaderTemplate from './permissions-by-filter-panel-header.hbs'
import PermissionsByFilterPanelContent from './permissions-by-filter-panel-content'

/**
 * The panels that contain principals on the dataset edit form.
 * Broken down by direct/indirect (rollups only)/view all records
 *
 * @property {Dataset} model
 */
const PermissionsByFilterPanel = Collapsible.extend({

	replace: true,

	/**
	 * @param {String|Function} [options.assigneesUrl] the url to load potential
	 * assignees with.  Defaults to `api/datasets/{id}/potential-assignees?includeActiveViewers=true`
	 *
	 * @param {Boolean} renderTemplateRollupSelect If a rollup select should be
	 * rendered for rollup template principals (false for indirect principals)
	 */
	initialize: function(options = {}) {
		this._applyViewState(options)
		Collapsible.prototype.initialize.apply(this, arguments)
		this.principals = this.options.currentPrincipals
	},

	_applyViewState: function(options = {}) {
		options.collapsed = _.result(options.viewState, 'panelCollapsed', true)
		options.tableCollapsed = _.result(options.viewState, 'tableCollapsed')
	},

	/**
	 * Allows the state of this view to be re-applied once a parent view is
	 * (re)rendered.  The attributes in this state object should not be used
	 * by any other components; treat this a a black box.
	 */
	getViewState: function() {
		return {
			panelCollapsed: this.collapsed(),
			tableCollapsed: !this.$('table').hasClass('is-expanded')
		}
	},

	render: function() {
		this.options.header = this._renderHeader()
		this.options.content = this._renderContent()
		Collapsible.prototype.render.apply(this, arguments)
		this._attachListeners()
		return this
	},

	_renderHeader: function() {
		return panelHeaderTemplate({
			header: i18n.get(this.options.headerKey, _.size(this.principals))
		})
	},

	_renderContent: function() {
		this.principalPanel = new PermissionsByFilterPanelContent(_.extend({
			placeholderKey: 'admin.group.addUserGroup',
			exclude: this.options.excludePrincipals,
			url: this.options.assigneesUrl,
			getPrincipalDataRow: this.options.principalDataRowFn
		}, this.options))

		this.principalPanel.on('before-add', this.trigger.bind(this))
		return this.principalPanel
	},

	_attachListeners: function() {
		this.principalPanel.on('change', () => this._update())
		this.principalPanel.on('change:add', () => this.trigger('add'))
		this.principalPanel.on('before-add', (principal, newTr) => {
			this.trigger('before-add', principal, newTr)
		})
		this.principalPanel.on('change:delete', (principal) => {
			this.trigger('delete', principal)
		})
		this.principalPanel.on('change:rollup-permission', (opts) => {
			this.trigger('change:rollup-permission', opts)
		})
		this.principalPanel.principalChooser.on('show', () => this._update())
	},

	toggleAddPrincipal: function(show=true) {
		this.$('.add').toggleClass('is-not-displayed',!show)
	},

	toggleVisible: function(visible) {
		this.$el.toggleClass('is-not-displayed', !visible)
	},

	updatePrincipals: function(principals) {
		this.principals = principals
		this.principalPanel.renderPrincipals(principals)
		this._update()
		this.principalPanel.addLinkToPrincipalLabels()
		this.trigger('updatePrincipals')
	},

	/**
	 * When something happens which will change the size of the content in the
	 * panel, call _update() in order to make sure the panel is sized correctly
	 * and the header is correct
	 */
	_update: function(noTransition) {
		this.toggleTransitions(false)
		this.resize()
		this._updateHeader()
		this.toggleTransitions(true)
	},

	_updateHeader: function() {
		this.$('.panel-header h3').html(i18n.get(this.options.headerKey, _.size(this.principalPanel.getPrincipals())))
	},

	setPlaceHolderText: function(text) {
		this.principalPanel.setPlaceHolderText(text)
	},

	remove: function() {
		Collapsible.prototype.remove.apply(this, arguments)
		_.invokeMap([this.principalPanel, this.tableExpander], 'remove')
	}
})

export default PermissionsByFilterPanel

PermissionsByFilterPanel.__filename__ = 'datasets/edit/permissions-by-filter-panel.js'
